"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  Code,
  IconArrowRight,
  IconArrowUpRight,
} from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import { familySnippet } from "./editorial";
import { pythonTokens } from "./python-tokens";
import {
  MODEL_FAMILIES,
  chipsForFamily,
  familyForSlug,
  formatMatrixDate,
  rowsForFamily,
  slugify,
} from "./photon-data";

const DOCS_URL = "https://docs.moondream.ai/running-locally";
const RESULTS_URL = "/photon/performance";
const LEGACY_RESULTS_URL =
  "https://github.com/m87-labs/kestrel/blob/main/PERFORMANCE.md";

const useLandingTheme = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";
  return { theme, toggle: () => setTheme(theme === "dark" ? "light" : "dark") };
};

export function ModelFamilyPage({ familySlug }: { familySlug: string }) {
  const { theme, toggle } = useLandingTheme();
  const [tab, setTab] = useState<"image" | "text">("image");
  const family = familyForSlug(familySlug);

  if (!family) return null; // route guarantees a valid slug

  const rows = rowsForFamily(family.name);
  const chips = chipsForFamily(family.name);
  const snippet = familySnippet(family.name);
  const multimodal = family.modalities.toLowerCase().includes("image");

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggle} />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 48 }}
        >
          <div className="container">
            <div
              className="t-eyebrow"
              style={{ color: "var(--text-tertiary)" }}
            >
              Photon · Models
            </div>
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              {family.name}
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              {family.description}
            </p>

            <dl
              className="family-meta"
              style={{ maxWidth: 560, marginTop: 24 }}
            >
              {family.releases.length > 0 ? (
                <div>
                  <dt>Releases</dt>
                  <dd className="family-releases">
                    {family.releases.map((release) => (
                      <span
                        key={release.name}
                        className={
                          release.status === "production"
                            ? "is-active"
                            : "is-inactive"
                        }
                      >
                        {release.name}
                      </span>
                    ))}
                  </dd>
                </div>
              ) : null}
              {family.versions.length > 0 ? (
                <div>
                  <dt>Sizes</dt>
                  <dd>{family.versions.join(" · ")}</dd>
                </div>
              ) : null}
              <div>
                <dt>Modalities</dt>
                <dd>{family.modalities}</dd>
              </div>
              <div>
                <dt>Introduced</dt>
                <dd>{family.introducedIn}</dd>
              </div>
            </dl>

            <div className="lens-cta" style={{ marginTop: 18 }}>
              <Link href="/photon/models" className="lens-cta-link">
                All models
              </Link>
              <Link href="/photon/support" className="lens-cta-link">
                Support explorer <IconArrowRight size={12} />
              </Link>
            </div>
          </div>
        </section>

        {snippet ? (
          <section
            className="section"
            style={{ paddingTop: 0, paddingBottom: 40 }}
          >
            <div className="container">
              <div className="photon-code-wrap">
                <div className="photon-code-head">
                  <div>
                    <div
                      className="t-eyebrow"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      Quickstart · pip install moondream
                    </div>
                    <div className="t-heading-md" style={{ marginTop: 4 }}>
                      Call {family.name} in a few lines.
                    </div>
                  </div>
                  {multimodal ? (
                    <div className="seg" role="group" aria-label="Modality">
                      <button
                        type="button"
                        className={tab === "image" ? "is-active" : ""}
                        onClick={() => setTab("image")}
                      >
                        Image + text
                      </button>
                      <button
                        type="button"
                        className={tab === "text" ? "is-active" : ""}
                        onClick={() => setTab("text")}
                      >
                        Text
                      </button>
                    </div>
                  ) : null}
                </div>
                <Code
                  lang="python"
                  tokens={pythonTokens(
                    tab === "image" && multimodal
                      ? snippet.imageText
                      : snippet.text,
                  )}
                />
                <p
                  className="t-caption mono"
                  style={{ color: "var(--text-tertiary)", marginTop: 12 }}
                >
                  <a
                    href={DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                  >
                    Full API reference
                  </a>{" "}
                  — docs.moondream.ai/running-locally
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <section className="section" style={{ paddingTop: 0, paddingBottom: 40 }}>
          <div className="container">
            <div
              className="t-eyebrow"
              style={{ color: "var(--text-tertiary)" }}
            >
              Runs on
            </div>
            <div className="family-runson">
              {chips.map((chip) => (
                <Link
                  key={chip}
                  href={`/photon/hardware/${slugify(chip)}`}
                  className="family-runson-chip"
                >
                  {chip}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0, paddingBottom: 64 }}>
          <div className="container">
            <div className="matrix-panel">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Chip</th>
                    <th>Tested hardware</th>
                    <th>Since</th>
                    <th>Last verified</th>
                    <th>Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} id={row.id}>
                      <td>
                        <span className="matrix-model-name">{row.model}</span>
                      </td>
                      <td>
                        <Link
                          href={`/photon/hardware/${slugify(row.chip)}`}
                          className="matrix-model"
                        >
                          {row.chip}
                        </Link>
                      </td>
                      <td className="matrix-dim">
                        {row.testedHardware ?? "—"}
                      </td>
                      <td className="mono tabular matrix-dim">
                        {row.since ? `v${row.since}` : "—"}
                      </td>
                      <td className="mono tabular matrix-dim">
                        {row.lastVerified
                          ? formatMatrixDate(row.lastVerified)
                          : "—"}
                      </td>
                      <td>
                        {row.benchmarked ? (
                          row.chip === "H100" ? (
                            <Link href={RESULTS_URL} className="family-cta ev-link">
                              Results <IconArrowUpRight size={11} />
                            </Link>
                          ) : (
                            <a
                              href={LEGACY_RESULTS_URL}
                              target="_blank"
                              rel="noreferrer"
                              className="family-cta ev-link"
                            >
                              Results <IconArrowUpRight size={11} />
                            </a>
                          )
                        ) : (
                          <span className="matrix-dim">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="install-strip" style={{ marginTop: 32 }}>
              <div>
                <div className="t-heading-sm">Run {family.name} with Photon</div>
                <p
                  className="t-body-sm"
                  style={{ color: "var(--text-secondary)", marginTop: 4 }}
                >
                  Install the SDK and pick any listed model.
                </p>
              </div>
              <InstallSnippet />
            </div>

            <p className="matrix-foot mono">
              <a href={DOCS_URL} target="_blank" rel="noreferrer">
                Quickstart docs
              </a>{" "}
              · <Link href="/photon/models">all models</Link> ·{" "}
              <Link href="/photon/updates">product updates</Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export function ModelsIndex() {
  const { theme, toggle } = useLandingTheme();

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggle} />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 48 }}
        >
          <div className="container">
            <div
              className="t-eyebrow"
              style={{ color: "var(--text-tertiary)" }}
            >
              Photon · Models
            </div>
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              Model families Photon runs.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              Every family with production support. Each page lists the
              architectures that family is validated on.
            </p>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="model-card-grid">
              {MODEL_FAMILIES.map((family) => (
                <Link
                  key={family.id}
                  href={`/photon/models/${family.id}`}
                  className="model-card"
                >
                  <div className="model-card-head">
                    <h3>{family.name}</h3>
                    <span>
                      {family.verifiedHardware.length}{" "}
                      {family.verifiedHardware.length === 1
                        ? "target"
                        : "targets"}
                    </span>
                  </div>
                  <p>{family.description}</p>
                  <div className="model-card-spec mono">
                    {family.versions.length > 0
                      ? family.versions.join(" · ")
                      : family.releases.map((r) => r.name).join(" · ")}{" "}
                    · {family.verifiedHardware.length}{" "}
                    {family.verifiedHardware.length === 1
                      ? "target"
                      : "targets"}
                  </div>
                  <div className="model-card-cta">
                    Supported hardware <IconArrowRight size={13} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
